import { Guide } from './guideTypes';

/* Building Assistant user guide.

   Describes what the assistant can and cannot answer. The scope statements
   below track bas-api's system prompt (bas/rag/engine.py SYSTEM) and the demo
   corpus — when that corpus or prompt changes, revise the numbers here. */

export const buildingAssistantGuide: Guide = {
  id: 'building-assistant',
  title: 'Building Assistant',
  subtitle: 'Asking a building about its own systems and health',
  version: '1.0',
  updated: '2026-09-08',
  intro:
    'The Building Assistant answers plain-language questions about a building’s mechanical systems '
    + 'and how they are behaving — "which sensors look stuck?", "how is AHU-2 doing?", "what has live '
    + 'trend data?". Every answer is grounded in that building’s BAS (building automation system) '
    + 'records and its live trend history; the assistant will say so plainly when it does not have '
    + 'the data rather than guess.',
  sections: [
    {
      id: 'what-it-answers',
      title: 'What it can answer',
      blocks: [
        {
          kind: 'bullets',
          items: [
            '**Equipment and points** — what air handlers, fan-coil units, pumps, meters and sensors '
            + 'exist, how they relate (what feeds what, what is part of what), and their types and units.',
            '**Sensor health** — which points look stuck or flatlined, which read outside a plausible '
            + 'range, and where the data has gaps, based on precomputed health summaries.',
            '**Live values** — the assistant can pull the actual trend for a point over a date range '
            + '("what did AHU-1’s discharge temp do in March 2021?") and cross-check several points.',
          ],
        },
        {
          kind: 'callout',
          tone: 'info',
          title: 'Demo building',
          text:
            'The current demo is "Site B" — a real office building with about 850 points, 49 of which '
            + 'have live trend history in the sample. Ask "what points have live trend data?" to see '
            + 'that list.',
        },
      ],
    },
    {
      id: 'limits',
      title: 'What it will not do',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'It never invents a point, a reading, or a piece of equipment. If a point has no live '
            + 'data, it says so instead of estimating from the point’s name.',
            'It is not a controls system — it cannot change setpoints or command equipment. All of '
            + 'its tools are read-only.',
            'It is not a substitute for a site inspection or a commissioning report; treat its '
            + 'findings as leads to verify.',
          ],
        },
      ],
    },
    {
      id: 'using-it',
      title: 'Using the assistant',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Open the **Building Assistant** tab in a project. Pick a building if more than one is '
            + 'available.',
            'Type a question, or tap one of the suggested prompts. Press **Enter** to send, '
            + '**Shift+Enter** for a new line.',
            'Watch the answer stream in. Chips show each live-data lookup the assistant makes while '
            + 'it works.',
            'Open **Evidence** under an answer to see the exact records it retrieved (with relevance '
            + 'scores) and every tool call it made.',
            'Use **New thread** to start over. Follow-up questions in the same thread keep context '
            + '("and for April?").',
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          text:
            'The assistant needs a connection — it is disabled offline. Its answers are generated '
            + 'fresh each time and are not saved with the project (yet).',
        },
      ],
    },
  ],
};
